import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, CheckCheck, AlertTriangle, Info, Check, Loader2 } from "lucide-react";
import { CounterProposeDialog } from "./CounterProposeDialog";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export function RescheduleTab() {
  const t = useT();
  const [hours, setHours] = useState("24");
  const [savedHours, setSavedHours] = useState(24);
  const [saving, setSaving] = useState(false);
  const [reqs, setReqs] = useState(null);
  const [busy, setBusy] = useState(null);
  const [rejectMsg, setRejectMsg] = useState({});
  const [counterFor, setCounterFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/reschedule-settings");
      setHours(String(data?.reschedule_auto_min_hours ?? 24));
      setSavedHours(Number(data?.reschedule_auto_min_hours ?? 24));
    } catch { /* keep defaults */ }
    try {
      const { data } = await api.get("/coach/reschedule-requests?status=pending");
      setReqs(data?.requests || []);
    } catch { setReqs([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const parsed = parseInt(hours || "0", 10);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 336) { toast.error(t("bk.rs_invalid")); return; }
    setSaving(true);
    try {
      await api.patch("/coach/reschedule-settings", { reschedule_auto_min_hours: parsed });
      setSavedHours(parsed);
      toast.success(t("bk.rs_saved", { n: parsed }));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const approve = async (r, chosen_window) => {
    setBusy(r.id);
    try { await api.post(`/coach/reschedule-requests/${r.id}/approve`, { chosen_window }); toast.success(t("bk.rs_approved_toast", { name: r.client_name })); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };
  const reject = async (r) => {
    setBusy(r.id);
    try { await api.post(`/coach/reschedule-requests/${r.id}/reject`, { message: (rejectMsg[r.id] || "").trim() || undefined }); toast.success(t("bk.rs_rejected_toast", { name: r.client_name })); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="bk-reschedule-tab">
      {/* Setting */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="mb-1 text-sm font-semibold text-foreground">{t("bk.rs_title")}</p>
        <p className="mb-3 text-xs text-muted-foreground">{t("bk.rs_sub")}</p>
        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("bk.rs_window_label")}</label>
        <div className="flex items-center gap-2">
          <Input type="number" min="0" max="336" value={hours} onChange={(e) => setHours(e.target.value)} className="h-10 w-28" data-testid="bk-rs-hours" />
          <span className="text-sm text-muted-foreground">{t("bk.rs_window_unit")}</span>
          <Button className="gap-1.5 rounded-full" disabled={saving || parseInt(hours || "0", 10) === savedHours} onClick={save} data-testid="bk-rs-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("bk.rf_save")}
          </Button>
        </div>
        <p className="mt-2.5 text-xs text-muted-foreground">{t("bk.rs_hint")}</p>
      </div>

      {/* Pending requests */}
      <h3 className="mb-3 mt-6 font-display text-lg text-foreground">{t("bk.rs_pending_title")}</h3>
      {reqs === null ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
      ) : reqs.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-10 text-center" data-testid="bk-rs-empty">
          <CheckCheck className="h-7 w-7 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">{t("bk.rs_pending_empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reqs.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`bk-rs-req-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-foreground">{r.client_name}</p>
                <p className="truncate text-sm text-muted-foreground">{r.session_title}</p>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("bk.rs_currently")}: {fmt(r.current_scheduled_at)}{r.duration_min ? ` · ${r.duration_min} min` : ""}</p>
              {r.no_auto_reason && (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-amber-600">
                  {r.no_auto_reason === "too_close" ? <AlertTriangle className="h-3.5 w-3.5" /> : <Info className="h-3.5 w-3.5" />}
                  {r.no_auto_reason === "too_close" ? t("bk.rs_reason_too_close") : t("bk.rs_reason_no_slot")}
                </p>
              )}
              {r.reason && <p className="mt-1 text-xs italic text-muted-foreground">&ldquo;{r.reason}&rdquo;</p>}

              <p className="mb-1.5 mt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("bk.rs_client_windows")}</p>
              <div className="space-y-1.5">
                {r.preferred_windows.map((w) => (
                  <div key={w} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-secondary/40 p-2.5">
                    <span className="flex items-center gap-1.5 text-sm text-foreground"><Clock className="h-3.5 w-3.5 text-muted-foreground" /> {fmt(w)}</span>
                    <Button size="sm" className="h-8 rounded-full" disabled={busy === r.id} onClick={() => approve(r, w)} data-testid={`bk-rs-approve-${r.id}`}>
                      {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("bk.rs_approve")}
                    </Button>
                  </div>
                ))}
              </div>

              <Textarea value={rejectMsg[r.id] || ""} onChange={(e) => setRejectMsg((p) => ({ ...p, [r.id]: e.target.value }))} maxLength={500} placeholder={t("bk.rs_reject_ph")} className="mt-3 min-h-[50px]" data-testid={`bk-rs-reject-msg-${r.id}`} />
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="outline" className="rounded-full" disabled={busy === r.id} onClick={() => setCounterFor(r)} data-testid={`bk-rs-counter-${r.id}`}>{t("bk.rs_counter")}</Button>
                <Button size="sm" variant="outline" className="rounded-full text-rose-600 hover:text-rose-700" disabled={busy === r.id} onClick={() => reject(r)} data-testid={`bk-rs-reject-${r.id}`}>{t("bk.rs_reject")}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {counterFor && <CounterProposeDialog request={counterFor} onClose={() => setCounterFor(null)} onSent={() => { setCounterFor(null); load(); }} />}
    </div>
  );
}
